import type {Moment} from 'moment-timezone'
import {capitalizeFirstLetter} from '@freckle/educator-entities/ts/common/helpers/string-helper'
import type {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Flex} from '@freckle/educator-materials/ts/components/flex'
import {Label} from '@freckle/educator-materials/ts/components/label'
import {H1} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {DateRangePicker} from '@freckle/school/ts/common/components/common/date-range-picker'
import {dayEnd, dayStart} from '@freckle/school/ts/common/components/common/date-range-picker/logic'
import {IdpPicker} from '@freckle/school/ts/common/components/common/idp-picker'
import type {PeriscopeEmbedData} from '@freckle/school/ts/common/components/common/periscope-embed'
import {PeriscopeEmbed} from '@freckle/school/ts/common/components/common/periscope-embed'
import {SchoolPicker} from '@freckle/school/ts/common/components/common/school-picker'
import {StudentPicker} from '@freckle/school/ts/common/components/common/student-picker'
import {TeacherPicker} from '@freckle/school/ts/common/components/common/teacher-picker'
import type {AllProvidersOrSpecificT} from '@freckle/school/ts/common/helpers/api-helper'
import {DomainPicker} from '@freckle/school/ts/pages/growth/components/domain-picker'
import {GradePicker} from '@freckle/school/ts/pages/growth/components/grade-picker'
import {SubjectButtonGroup} from '@freckle/school/ts/pages/growth/components/subject-button-group'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'
import {DEFAULT_ELA_MIN_DATE} from './constants'
import {
  subjectDropdown,
  wrapper,
  printButton,
  filters,
  reportDescription,
  subjectPickerLabel
} from './growth.module.scss'

type Props = {
  subject: SubjectT
  onChangeSubject: (subject: SubjectT) => void
  schoolId: number | undefined | null
  schools: Array<SchoolAttrs>
  onChangeSchool: (schoolId: number) => void
  onAllSchools: () => void
  teachers: Array<BaseTeacherAttrs> | undefined | null
  teacherId: number | undefined | null
  onChangeTeacher: (teacherId?: number) => void
  students: Array<StudentAttrs> | undefined | null
  studentId: number | undefined | null
  onChangeStudent: (studentId?: number | null) => void
  idpOrigins: AllProvidersOrSpecificT
  onChangeIdp: (a: AllProvidersOrSpecificT) => void
  startDate: Moment
  endDate: Moment
  onChangeStartDate: (date: Moment) => void
  onChangeEndDate: (date: Moment) => void
  grade: number | undefined | null
  onChangeGrade: (grade?: number | null) => void
  domain: string | undefined | null
  onChangeDomain: (domain?: string | null) => void
  mathDomains: Array<RlDomainT>
  elaDomains: Array<RlDomainT>
  onDrilldown: (filters: {[filter: string]: string}) => void
  mathStandardSet: RlStandardSetT
  schoolAdmin: SchoolAdminAttrs
}

export function Growth({
  subject,
  onChangeSubject,
  schoolId,
  schools,
  onChangeSchool,
  onAllSchools,
  teachers,
  teacherId,
  onChangeTeacher,
  students,
  studentId,
  onChangeStudent,
  idpOrigins,
  onChangeIdp,
  startDate,
  endDate,
  onChangeStartDate,
  onChangeEndDate,
  grade,
  onChangeGrade,
  domain,
  onChangeDomain,
  mathDomains,
  elaDomains,
  onDrilldown,
  mathStandardSet,
  schoolAdmin
}: Props) {
  const {t} = useSafeTranslation()

  const periscopeEmbedData = getPeriscopeEmbedData(
    subject,
    schoolId,
    teacherId,
    studentId,
    idpOrigins,
    startDate,
    endDate,
    grade,
    domain
  )

  const subjectPicker = (
    <div className={subjectDropdown}>
      <Label className={subjectPickerLabel}>{t('GROWTH_PAGE_SUBJECT_LABEL_TEXT')}</Label>
      <div>
        <SubjectButtonGroup
          shouldShowEla={schoolAdmin.language !== 'en-GB'}
          selectedSubject={subject}
          onSelectSubject={onChangeSubject}
        />
      </div>
    </div>
  )

  const [currentSchoolId, showingAllSchools] =
    schoolId !== null && schoolId !== undefined ? [schoolId, false] : [0, true]

  const schoolPicker = (
    <SchoolPicker
      schools={schools}
      currentSchoolId={currentSchoolId}
      onChange={onChangeSchool}
      onAllSchools={onAllSchools}
      showingAllSchools={showingAllSchools}
    />
  )

  const teacherPicker =
    teachers !== null && teachers !== undefined && teachers.length > 0 ? (
      <TeacherPicker
        teachers={teachers}
        currentTeacherId={teacherId}
        onChangeTeacher={onChangeTeacher}
      />
    ) : null

  const studentPicker =
    students !== null && students !== undefined && students.length > 0 ? (
      <StudentPicker
        students={students}
        currentStudentId={studentId}
        onChangeStudent={onChangeStudent}
      />
    ) : null

  const gradePicker = <GradePicker currentGrade={grade} onChangeGrade={onChangeGrade} />

  const domainNames =
    subject === 'math'
      ? mathDomains.map(domain => domain.name)
      : elaDomains.map(domain => domain.name)
  const domainConcept =
    subject === 'math'
      ? capitalizeFirstLetter(mathStandardSet.domainLabel)
      : t('GROWTH_PAGE_DOMAIN_LABEL_TEXT')
  const domainsConcept =
    subject === 'math'
      ? capitalizeFirstLetter(mathStandardSet.domainsLabel)
      : t('GROWTH_PAGE_DOMAINS_LABEL_TEXT')
  const domainPicker = (
    <DomainPicker
      domains={domainNames}
      domainConcept={domainConcept}
      domainsConcept={domainsConcept}
      currentDomain={domain}
      onChangeDomain={onChangeDomain}
    />
  )

  const lastDayOf2022SchoolYear = {
    minDate: DEFAULT_ELA_MIN_DATE
  }

  const schoolYear2021SinceThisReportWillNoLongerWorkAfterMaxDateEla =
    subject === 'math' ? {} : lastDayOf2022SchoolYear

  return (
    <div className={wrapper}>
      <H1>{t('GROWTH_PAGE_TITLE')}</H1>

      <Flex justifyContent="flex-end">
        <Button className={`hidden-print ${printButton}`} style="tertiary" onClick={window.print}>
          <i className="fa fa-print" /> {t('GROWTH_PAGE_PRINT_BUTTON_TEXT')}
        </Button>
      </Flex>

      <div className={`hidden-print ${filters}`}>
        {subjectPicker}
        {schoolPicker}
        {teacherPicker}
        {studentPicker}
        {gradePicker}
        {domainPicker}
        <DateRangePicker
          {...schoolYear2021SinceThisReportWillNoLongerWorkAfterMaxDateEla}
          startDate={startDate}
          endDate={endDate}
          onChangeStartDate={onChangeStartDate}
          onChangeEndDate={onChangeEndDate}
        />
        <IdpPicker
          allSchoolIds={schools.map(s => s.id)}
          adminId={schoolAdmin.id}
          currentIdps={idpOrigins}
          onChange={onChangeIdp}
        />
      </div>
      <p className={reportDescription}>{t('GROWTH_PAGE_REPORT_DESCRIPTION_TEXT', {subject})}</p>
      <PeriscopeEmbed data={periscopeEmbedData} onDrilldown={onDrilldown} />
    </div>
  )
}

function getPeriscopeEmbedData(
  subject: SubjectT,
  schoolId: number | undefined | null,
  teacherId: number | undefined | null,
  studentId: number | undefined | null,
  idpOrigins: AllProvidersOrSpecificT,
  startDateUnclamped: Moment,
  endDateUnclamped: Moment,
  grade?: number | null,
  domain?: string | null
): PeriscopeEmbedData {
  const startDate = dayStart(startDateUnclamped)
  const endDate = dayEnd(endDateUnclamped)
  if (studentId !== null && studentId !== undefined) {
    return {
      tag: 'student_growth',
      subject,
      studentId,
      idpOrigins,
      startDate,
      endDate,
      domain
    }
  } else if (teacherId !== null && teacherId !== undefined) {
    return {
      tag: 'teacher_growth',
      subject,
      teacherId,
      idpOrigins,
      startDate,
      endDate,
      grade,
      domain
    }
  } else if (schoolId !== null && schoolId !== undefined) {
    return {
      tag: 'school_growth',
      subject,
      schoolId,
      idpOrigins,
      startDate,
      endDate,
      grade,
      domain
    }
  } else {
    return {
      tag: 'district_growth',
      subject,
      idpOrigins,
      startDate,
      endDate,
      grade,
      domain
    }
  }
}
